import { z } from "zod";

// Base schema for generic EagleView responses
export const EagleViewResponseSchema = z.object({});

// Type for generic EagleView responses
export type EagleViewResponse = z.infer<typeof EagleViewResponseSchema>;

// Product schema
export const EagleViewProductSchema = z.object({
  productID: z.number().nullable().optional(),
  name: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  productGroup: z.string().nullable().nullable().optional(),
  isTemporarilyUnavailable: z.boolean().nullable().optional(),
  priceMin: z.number().nullable().optional(),
  priceMax: z.number().nullable().optional(),
  deliveryProducts: z.array(z.object({
    productID: z.number().nullable().optional(),
    name: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    isTemporarilyUnavailable: z.boolean().nullable().optional(),
    priceMin: z.number().nullable().optional(),
    priceMax: z.number().nullable().optional(),
  })).nullable().optional(),
  addOnProducts: z.array(z.object({
    productID: z.number().nullable().optional(),
    name: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    isTemporarilyUnavailable: z.boolean().nullable().optional(),
    priceMin: z.number().nullable().optional(),
    priceMax: z.number().nullable().optional(),
  })).nullable().optional(),
  TypeOfStructure: z.number().nullable().optional(),
  IsRoofProduct: z.boolean().nullable().optional(),
  SortOrder: z.number().nullable().optional(),
  AllowsUserSubmittedPhotos: z.boolean().nullable().optional(),
});

// Type for EagleView product
export type EagleViewProduct = z.infer<typeof EagleViewProductSchema>;

// Report schema
export const EagleViewReportSchema = z.object({
  ReportId: z.number(),
  Street: z.string().nullable().optional(),
  BuildingId: z.string().nullable().optional(),
  City: z.string().nullable().optional(),
  State: z.string().nullable().optional(),
  Zip: z.string().nullable().optional(),
  Latitude: z.number().nullable().optional(),
  Longitude: z.number().nullable().optional(),
  ClaimNumber: z.string().nullable().optional(),
  ClaimInfo: z.string().nullable().optional(),
  BatchId: z.string().nullable().optional(),
  CatId: z.string().nullable().optional(),
  DatePlaced: z.string().nullable().optional(),
  DateCompleted: z.string().nullable().optional(),
  PONumber: z.string().nullable().optional(),
  Comments: z.string().nullable().optional(),
  ReferenceId: z.string().nullable().optional(),
  InsuredName: z.string().nullable().optional(),
  Status: z.string().nullable().optional(),
  Area: z.string().nullable().optional(),
  Pitch: z.string().nullable().optional(),
  ProductPrimaryId: z.number().nullable().optional(),
  ProductPrimary: z.string().nullable().optional(),
  ProductDeliveryId: z.number().nullable().optional(),
  ProductDelivery: z.string().nullable().optional(),
  AddOnProductIds: z.string().nullable().optional(),
  AllowsUserSubmittedPhotos: z.boolean().nullable().optional(),
  CanCancelReport: z.boolean().nullable().optional(),
  TotalRoofFacets: z.string().nullable().optional(),
  EligibleForUpgrade: z.boolean().nullable().optional(),
  ReportDownloadLink: z.string().nullable().optional(),
  LengthEave: z.string().nullable().optional(),
  LengthHip: z.string().nullable().optional(),
  LengthRake: z.string().nullable().optional(),
  LengthRidge: z.string().nullable().optional(),
  LengthValley: z.string().nullable().optional(),
});

// Type for EagleView report
export type EagleViewReport = z.infer<typeof EagleViewReportSchema>;

// PriceOrder Request Schema
export const PriceOrderSchema = z.object({
  OrderReports: z.object({
    ReportAddresses: z.object({
      Address: z.string(),
      City: z.string(),
      State: z.string(),
      Zip: z.string(),
      Country: z.string().nullable().optional(),
      Latitude: z.number().nullable().optional(),
      Longitude: z.number().nullable().optional(),
      AddressType: z.number(),
      VerifierUsedId: z.number().nullable().optional(),
      MapperUsedId: z.number().nullable().optional(),
      VerificationResultTypeId: z.number().nullable().optional()
    }).array(),
    BuildingId: z.string().nullable().optional(),
    PrimaryProductId: z.number(),
    DeliveryProductId: z.number(),
    AddOnProductIds: z.array(z.number()).nullable().optional(),
    MeasurementInstructionType: z.number(),
    ReportAttibutes: z.array(z.object({
      Attribute: z.number(),
      Value: z.string()
    })).nullable().optional(),
    ClaimNumber: z.string().nullable().optional(),
    ClaimInfo: z.string().nullable().optional(),
    BatchId: z.string().nullable().optional(),
    CatId: z.string().nullable().optional(),
    ChangesInLast4Years: z.boolean(),
    PONumber: z.string().nullable().optional(),
    Comments: z.string().nullable().optional(),
    ReferenceId: z.string().nullable().optional(),
    InsuredName: z.string().nullable().optional(),
    UpgradeFromReportId: z.number().nullable().optional(),
    PolicyNumber: z.string().nullable().optional(),
    DateOfLoss: z.string().nullable().optional(),
  }).array(),
  PromoCode: z.string().nullable().optional(),
});

export type PriceOrderRequest = z.infer<typeof PriceOrderSchema>;

// Price Order Response Schema
export const PriceOrderResponseSchema = z.object({
  MinPrice: z.number().nullable().optional(),
  MaxPrice: z.number().nullable().optional(),
  ReportPriceQuotes: z.object({
    PrimaryProductPriceQuote: z.object({
      ProductId: z.number().nullable().optional(),
      MinPrice: z.number().nullable().optional(),
      MaxPrice: z.number().nullable().optional(),
    }).nullable().optional(),
    DeliveryProductPriceQuote: z.object({
      ProductId: z.number().nullable().optional(),
      MinPrice: z.number().nullable().optional(),
      MaxPrice: z.number().nullable().optional(),
    }).nullable().optional(),
    AddOnProductPriceQuotes: z.array(z.object({
      ProductId: z.number().nullable().optional(),
      MinPrice: z.number().nullable().optional(),
      MaxPrice: z.number().nullable().optional(),
    })).nullable().optional(),
  }).nullable().optional(),
  Discounts: z.object({
    Description: z.string().nullable().optional(),
    MinAmount: z.number().nullable().optional(),
    MaxAmount: z.number().nullable().optional(),
    RejectionMessage: z.string().nullable().optional(),
  }).nullable().optional(),
});

export type PriceOrderResponse = z.infer<typeof PriceOrderResponseSchema>;

// Place Order Request Schema (extends PriceOrderSchema with credit card data)
export const PlaceOrderSchema = PriceOrderSchema.extend({
  PlaceOrderUser: z.string().nullable().optional(),
  CreditCardData: z.object({
    CardFirstName: z.string().nullable().optional(),
    CardLastName: z.string().nullable().optional(),
    ExpirationMonth: z.number().nullable().optional(),
    ExpirationYear: z.number().nullable().optional(),
    CreditCardNumber: z.string().nullable().optional(),
    CreditCardType: z.number().nullable().optional(),
  }).nullable().optional(),
});

export type PlaceOrderRequest = z.infer<typeof PlaceOrderSchema>;

// Place Order Response Schema
export const PlaceOrderResponseSchema = z.object({
  OrderId: z.number().nullable().optional(),
  ReportIds: z.array(z.number()).nullable().optional(),
});

export type PlaceOrderResponse = z.infer<typeof PlaceOrderResponseSchema>;

// Account Details Response Schema
export const AccountDetailsResponseSchema = z.object({
  billingExists: z.boolean().nullable().optional(),
});

export type AccountDetailsResponse = z.infer<typeof AccountDetailsResponseSchema>;

// Solar Product Availability Request Schema
export const SolarProductAvailabilitySchema = z.object({
  Address: z.string(),
  Latitude: z.number(),
  Longitude: z.number(),
  ProductList: z.array(z.number()),
  VintageExtension: z.boolean().nullable().optional(),
});

export type SolarProductAvailabilityRequest = z.infer<typeof SolarProductAvailabilitySchema>;

// Solar Product Availability Response Schema
export const SolarProductAvailabilityResponseSchema = z.object({
  JobId: z.string().nullable().optional(),
  Address: z.string().nullable().optional(),
  Latitude: z.number().nullable().optional(),
  Longitude: z.number().nullable().optional(),
  AvailabilityStatus: z.array(z.object({
    isAvailable: z.boolean().nullable().optional(),
    productId: z.number().nullable().optional(),
  })).nullable().optional(),
  JobStatus: z.string().nullable().optional(),
  RequestId: z.string().nullable().optional(),
});

export type SolarProductAvailabilityResponse = z.infer<typeof SolarProductAvailabilityResponseSchema>;

// Report Upgrade Products Response Schema
export const ReportUpgradeProductsResponseSchema = z.object({
  AvailableProducts: EagleViewProductSchema.nullable().optional(),
  CurrentOrder: z.object({
    ReportId: z.number().nullable().optional(),
    MeasurementRequestTypeId: z.number().nullable().optional(),
    ProductId: z.number().nullable().optional(),
    ProductDeliveryId: z.number().nullable().optional(),
    AddOnProductIds: z.array(z.number()).nullable().optional(),
    AdditionalEmails: z.string().nullable().optional(),
    ClaimNumber: z.string().nullable().optional(),
    ClaimInfo: z.string().nullable().optional(),
    PONumber: z.string().nullable().optional(),
    DateOfLoss: z.string().nullable().optional(),
    CatId: z.string().nullable().optional(),
  }).nullable().optional(),
});

export type ReportUpgradeProductsResponse = z.infer<typeof ReportUpgradeProductsResponseSchema>;

// Place Upgrade Order Request Schema
export const PlaceUpgradeOrderSchema = z.object({
  ReportId: z.number(),
  MeasurementRequestTypeId: z.number(),
  ProductId: z.number(),
  ProductDeliveryId: z.number(),
  AddOnProductIds: z.array(z.number()).nullable().optional(),
  AdditionalEmails: z.string().nullable().optional(),
  ClaimNumber: z.string().nullable().optional(),
  ClaimInfo: z.string().nullable().optional(),
  PONumber: z.string().nullable().optional(),
  DateOfLoss: z.string().nullable().optional(),
  CatId: z.string().nullable().optional(),
});

export type PlaceUpgradeOrderRequest = z.infer<typeof PlaceUpgradeOrderSchema>;

// Get Reports Request Schema
export const GetReportsSchema = z.object({
  productsToFiterBy: z.array(z.number()),
  statusesToFilterBy: z.string().nullable().optional(),
  sortBy: z.string().nullable().optional(),
  sortAscending: z.boolean().nullable().optional(),
  subStatusToFilterBy: z.string().nullable().optional(),
  fieldsToFilterBy: z.array(z.string()).nullable().optional(),
  textToFilterBy: z.string().nullable().optional(),
  referenceId: z.string().nullable().optional(),
  emailCC: z.string().nullable().optional(),
  fromDate: z.string().nullable().optional(),
  toDate: z.string().nullable().optional(),
});

export type GetReportsRequest = z.infer<typeof GetReportsSchema>;

// Get Reports Response Schema
export const GetReportsResponseSchema = z.object({
  ReportList: z.array(z.object({
    Id: z.number().nullable().optional(),
    Street1: z.string().nullable().optional(),
    Street2: z.string().nullable().optional(),
    City: z.string().nullable().optional(),
    State: z.string().nullable().optional(),
    Zip: z.string().nullable().optional(),
    Latitude: z.number().nullable().optional(),
    Longitude: z.number().nullable().optional(),
    ClaimNumber: z.string().nullable().optional(),
    ClaimInfo: z.string().nullable().optional(),
    BatchId: z.string().nullable().optional(),
    CatId: z.string().nullable().optional(),
    DatePlaced: z.string().nullable().optional(),
    DateCompleted: z.string().nullable().optional(),
    ReportProducts: z.object({
      ProductPrimaryId: z.number().nullable().optional(),
      ProductPrimary: z.string().nullable().optional(),
      ProductDelivery: z.string().nullable().optional(),
      ProductDeliveryId: z.number().nullable().optional(),
    }).nullable().optional(),
    ReportStatus: z.object({
      StatusId: z.number().nullable().optional(),
      Status: z.string().nullable().optional(),
      SubStatusId: z.number().nullable().optional(),
      SubStatus: z.string().nullable().optional(),
      DisplayStatus: z.string().nullable().optional(),
    }).nullable().optional(),
    ReportDownloadLink: z.string().nullable().optional(),
    EligibleForUpgrade: z.boolean().nullable().optional(),
    numberOfImages: z.number().nullable().optional(),
    addOns: z.object({
      productID: z.number().nullable().optional(),
      name: z.string().nullable().optional(),
      description: z.string().nullable().optional(),
    }).nullable().optional(),
    AllowsUserSubmittedPhotos: z.boolean().nullable().optional(),
    CanCancelReport: z.boolean().nullable().optional(),
    EmailCC: z.string().nullable().optional(),
    ReferenceId: z.string().nullable().optional(),
  })).nullable().optional(),
  TotalOfReports: z.number().nullable().optional(),
});

export type GetReportsResponse = z.infer<typeof GetReportsResponseSchema>;

// Structure Waste Measurements Response Schema
export const StructureWasteMeasurementsResponseSchema = z.object({
  StructureWasteTables: z.array(z.object({
    Complexity: z.string().nullable().optional(),
    StructureNumber: z.number().nullable().optional(),
    ModelId: z.string().nullable().optional(),
    AllPitchesUnderThreshold: z.boolean().nullable().optional(),
    StructureWasteTable: z.object({
      "Waste %": z.string().nullable().optional(),
      "Area (sq ft)": z.string().nullable().optional(),
      "Squares *": z.string().nullable().optional(),
      "Measured": z.string().nullable().optional(),
    }).nullable().optional(),
  })).nullable().optional(),
});

export type StructureWasteMeasurementsResponse = z.infer<typeof StructureWasteMeasurementsResponseSchema>;

// Update Report Address Request Schema
export const UpdateReportAddressSchema = z.object({
  Address: z.string(),
  City: z.string(),
  State: z.string(),
  Zip: z.string(),
  Country: z.string().nullable().optional(),
  Latitude: z.number().nullable().optional(),
  Longitude: z.number().nullable().optional(),
  AddressType: z.number(),
});

export type UpdateReportAddressRequest = z.infer<typeof UpdateReportAddressSchema>;

// Update Report Address Response Schema
export const UpdateReportAddressResponseSchema = UpdateReportAddressSchema.extend({
  VerifierUsedId: z.string().nullable().optional(),
  MapperUsedId: z.string().nullable().optional(),
  VerificationResultTypeId: z.string().nullable().optional(),
});

export type UpdateReportAddressResponse = z.infer<typeof UpdateReportAddressResponseSchema>;

// File Links Response Schema
export const FileLinksResponseSchema = z.object({
  Links: z.object({
    Link: z.string().nullable().optional(),
    ExpireTimestamp: z.string().nullable().optional(),
    FileType: z.string().nullable().optional(),
  }).nullable().optional(),
});

export type FileLinksResponse = z.infer<typeof FileLinksResponseSchema>; 